// src/components/TopNav.js
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FiMenu,
  FiX,
  FiLock,
  FiSettings,
  FiChevronDown,
  FiChevronRight,
} from "react-icons/fi";
import { useAuth } from "./AuthContext";

const NAV_ITEMS = [
  { label: "Home", path: "/dashboard" },
  { label: "Customers", path: "/customers" },
  { label: "Products", path: "/products" },
  { label: "Purchase", path: "/purchases" },
  { label: "Order", path: "/orders" },
  { label: "Invoices", path: "/invoices" },
  { label: "Credit Notes", path: "/creditNotes" },
  { label: "Doctors", path: "/doctors" },
  { label: "Payments", path: "/payments" },
  { label: "Expense", path: "/expense" },
  { label: "Visits", path: "/visits" },
  { label: "Reports", path: "/reports/reportmodule" },


];

export default function TopNav() {
  const { logout, user } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false); // desktop dropdown
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false); // mobile accordion

  const isActive = (path) => location.pathname.startsWith(path);
  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  return (
    <header className="sticky top-0 z-50 bg-gradient-to-r from-blue-900 to-blue-700 text-white shadow-md backdrop-blur">
      <div className="mx-auto flex max-w-screen-2xl items-center justify-between px-4 py-3">
        {/* Left - Logo + Agency */}
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-md bg-white/20 font-bold shadow-inner">
            W
          </div>
          <div className="leading-tight text-sm">
            <div className="font-bold tracking-wide">
              WECARE INDIA <span className="text-sky-300">Agency</span>
            </div>
            <div className="text-xs text-white/70">Pharma / Medical ERP</div>
          </div>
        </div>

        {/* Center - Nav Links (Desktop) */}
        <nav className="hidden items-center gap-1 md:flex">
          {NAV_ITEMS.map(({ label, path }) => (
            <Link
              key={path}
              to={path}
              className={`px-3 py-2 text-sm font-medium transition-all rounded-md ${
                isActive(path)
                  ? "bg-yellow-400 text-blue-900 shadow-sm"
                  : "hover:bg-white/20"
              }`}
            >
              {label}
            </Link>
          ))}

          {/* Admin-only Settings dropdown */}
          {isAdmin && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setSettingsOpen((v) => !v)}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-medium transition-all rounded-md ${
                  settingsOpen ? "bg-white/20" : "hover:bg-white/20"
                }`}
                aria-haspopup="menu"
                aria-expanded={settingsOpen}
              >
                <FiSettings />
                Settings
                <FiChevronDown
                  className={`transition-transform ${
                    settingsOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {settingsOpen && (
                <div
                  className="absolute right-0 mt-2 w-56 rounded-lg bg-white text-slate-800 shadow-lg ring-1 ring-black/5"
                  role="menu"
                >
                  <Link
                    to="/admin/UploadsTabs"
                    onClick={() => {
                      setSettingsOpen(false);
                      setMenuOpen(false);
                    }}
                    className="block px-3 py-2 text-sm hover:bg-slate-50 rounded-t-lg"
                    role="menuitem"
                  >
                    Bulk Upload
                  </Link>
                  <Link
                    to="/reports/validation"
                    onClick={() => {
                      setSettingsOpen(false);
                      setMenuOpen(false);
                    }}
                    className="block px-3 py-2 text-sm hover:bg-slate-50 rounded-b-lg"
                    role="menuitem"
                  >
                    Validation Reports
                  </Link>
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Right - Logout Button (Desktop) */}
        <div className="hidden items-center md:flex">
          <button
            onClick={logout}
            className="flex items-center gap-2 rounded-lg bg-red-500 px-3 py-2 text-sm text-white shadow hover:bg-red-600"
          >
            <FiLock size={16} />
            Logout
          </button>
        </div>

        {/* Mobile Menu Button */}
        <div className="flex items-center gap-2 md:hidden">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="text-white hover:text-yellow-400"
            aria-label="Toggle navigation"
          >
            {menuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Dropdown Menu */}
      {menuOpen && (
        <nav className="space-y-1 bg-blue-800 px-4 pb-4 text-sm md:hidden transition-all">
          {NAV_ITEMS.map(({ label, path }) => (
            <Link
              key={path}
              to={path}
              onClick={() => setMenuOpen(false)}
              className={`block rounded-md px-3 py-2 ${
                isActive(path)
                  ? "bg-yellow-300 text-blue-900"
                  : "hover:bg-white/20"
              }`}
            >
              {label}
            </Link>
          ))}

          {/* Admin-only Settings accordion */}
          {isAdmin && (
            <div className="mt-1 rounded-md">
              <button
                onClick={() => setMobileSettingsOpen((v) => !v)}
                className="flex w-full items-center justify-between rounded-md px-3 py-2 hover:bg-white/10"
              >
                <span className="inline-flex items-center gap-2">
                  <FiSettings />
                  Settings
                </span>
                <FiChevronRight
                  className={`transition-transform ${
                    mobileSettingsOpen ? "rotate-90" : ""
                  }`}
                />
              </button>
              {mobileSettingsOpen && (
                <div className="ml-4 space-y-1 py-1">
                  <Link
                    to="/admin/UploadsTabs"
                    onClick={() => setMenuOpen(false)}
                    className="block rounded-md px-3 py-2 hover:bg-white/10"
                  >
                    Bulk Upload
                  </Link>
                  <Link
                    to="/reports/validation"
                    onClick={() => setMenuOpen(false)}
                    className="block rounded-md px-3 py-2 hover:bg-white/10"
                  >
                    Validation Reports
                  </Link>
                </div>
              )}
            </div>
          )}

          <button
            onClick={logout}
            className="mt-2 w-full rounded-md bg-red-500 px-3 py-2 text-left text-white hover:bg-red-600"
          >
            <FiLock className="mr-2 inline" />
            Logout
          </button>
        </nav>
      )}
    </header>
  );
}
